import { useState } from 'react';
import { CastButton } from './CastButton.js';
import { tvLinkModel, type TvConfig } from './tvLinkModel.js';
import { useSenderCast } from '../lib/useGoogleCast.js';
import { copyText } from '../lib/clipboard.js';
import { applePlatform } from '../lib/platform.js';
import styles from './TableTv.module.css';

/**
 * One table's TV controls, inline in its console row: the address that table's
 * screen can actually open, a copy button for the group chat, and — where the
 * browser can — an in-page cast pointed at that same screen. The venue has no
 * TV of its own; every screen in the room belongs to a table.
 *
 * There is no "open" here on purpose. Opening the TV page on the console's own
 * machine shows the owner a screen nobody else is looking at; the address is
 * for the TV to type or for the cast button to reach, so the row prints it and
 * hands over the two things that get it onto a screen in the room.
 *
 * The `/api/config` answer is fetched once by the console and handed down, so
 * a room full of tables asks the server once, not once per row — including
 * `castAppId`, which is what lets Chrome on Android actually cast.
 */
export function TableTv({
  tvPath,
  config,
  origin = window.location.origin,
}: {
  tvPath: string;
  config: TvConfig | null;
  origin?: string;
}) {
  const [copied, setCopied] = useState<boolean | null>(null);
  const vm = tvLinkModel({ origin, tvPath, config, apple: applePlatform() });
  // Native Cast when the console was handed CAST_APP_ID (the only path
  // Chrome on Android will take); Presentation API everywhere else.
  const cast = useSenderCast(vm.castUrl, config?.castAppId ?? null);

  return (
    <span className={styles.wrap}>
      <span className={styles.url}>{vm.openUrlShort}</span>
      <span className={styles.buttons}>
        <button
          type="button"
          className={styles.copy}
          onClick={() => void copyText(vm.openUrl).then(setCopied)}
        >
          {copied === null ? 'Copy TV link' : copied ? 'Copied' : 'Copy failed'}
        </button>
        <CastButton cast={cast} blockedReason={vm.castBlocked} compact />
      </span>
    </span>
  );
}
